import type { PaymentMethod } from '@fitness-force/models';
import { PayModeLevel1, PayModeLevel2 } from '@fitness-force/enum';
import { TranslateService } from '@ngx-translate/core';

export interface PaymentMethodDisplayData {
  img: string;
  icon: string;
  number: string;
  level1Text?: string;
  networkType?: string;
}

/**
 * Get payment method display details including image, icon, and formatted number
 * @param paymentMethod - The payment method object
 * @param translate - TranslateService instance for i18n
 * @returns PaymentMethodDisplayData object with display information
 */
export function getPaymentMethodDetails(
  paymentMethod: PaymentMethod,
  translate: TranslateService
): PaymentMethodDisplayData {
  const paymentData: PaymentMethodDisplayData = {
    img: '',
    icon: '',
    number: '',
    level1Text: '',
    networkType: paymentMethod.network_type || ''
  };

  const data = PayModeLevel1[paymentMethod.level1.toString() as keyof typeof PayModeLevel1];
  const level2 = PayModeLevel2[paymentMethod.level2.toString() as keyof typeof PayModeLevel2];

  switch (data) {
    case PayModeLevel1.Cash:
      paymentData.img = '/assets/img/icons/icon-cash.svg';
      paymentData.icon = 'fab fa-cc-visa';
      paymentData.level1Text = 'Cash';
      translate
        .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: '****' })
        .subscribe((translations: any) => {
          paymentData.number = translations;
        });
      break;

    case PayModeLevel1.ACH:
      paymentData.img = '/assets/img/ach-options.svg.png';
      paymentData.icon = 'fab fa-cc-visa';
      paymentData.level1Text = 'ACH';
      translate
        .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: '****' })
        .subscribe((translations: any) => {
          paymentData.number = translations;
        });
      break;

    case PayModeLevel1.Adjustment:
      paymentData.img = '/assets/img/icons/icon-adjustment.svg';
      paymentData.icon = 'fab fa-cc-visa';
      paymentData.level1Text = 'Adjustment';
      translate
        .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: '****' })
        .subscribe((translations: any) => {
          paymentData.number = translations;
        });
      break;

    case PayModeLevel1.Card:
      if (paymentMethod.network_type?.toLowerCase() === 'mastercard') {
        paymentData.img = '/assets/img/mastercard.png';
      } else if (paymentMethod.network_type?.toLowerCase() === 'visa') {
        paymentData.img = '/assets/img/visa.png';
      } else if (paymentMethod.network_type?.toLowerCase() === 'amex') {
        paymentData.img = '/assets/img/amex.png';
      } else {
        paymentData.img = '/assets/img/icons/icon-card.svg';
      }
      paymentData.icon = 'fab fa-cc-visa';
      paymentData.level1Text = 'Card';
      translate
        .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: paymentMethod.masked_card_number })
        .subscribe((translations: any) => {
          paymentData.number = translations;
        });
      break;

    case PayModeLevel1.Cheque:
      paymentData.img = '/assets/img/icons/icon-cheque.svg';
      paymentData.icon = 'fab fa-cc-visa';
      paymentData.level1Text = 'Cheque';
      translate
        .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: '****' })
        .subscribe((translations: any) => {
          paymentData.number = translations;
        });
      break;

    case PayModeLevel1.Emandate:
      paymentData.img = '/assets/img/icons/icon-emandate.svg';
      paymentData.icon = 'fab fa-cc-visa';
      paymentData.level1Text = 'E-Mandate';
      translate
        .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: '****' })
        .subscribe((translations: any) => {
          paymentData.number = translations;
        });
      break;

    case PayModeLevel1.NetBanking:
      paymentData.img = '/assets/img/bank-payment-img.svg';
      paymentData.icon = 'fab fa-cc-visa';
      paymentData.level1Text = 'NetBanking';
      translate
        .get('SUBSCRIPTIONS.CARD-ENDING-WITH', {
          number: paymentMethod.masked_account_number ? paymentMethod.masked_account_number : '****'
        })
        .subscribe((translations: any) => {
          paymentData.number = translations;
        });
      break;

    case PayModeLevel1.UPI:
      paymentData.img = '/assets/img/logo-upi.svg';
      paymentData.icon = 'fab fa-cc-visa';
      paymentData.level1Text = 'UPI';
      paymentData.number = paymentMethod.vpa_id;
      break;

    case PayModeLevel1.Wallet:
      paymentData.level1Text = 'Wallet';
      if (level2 === PayModeLevel2.ApplePay) {
        paymentData.img = '/assets/img/apple-pay-logo.svg';
        paymentData.icon = 'fab fa-apple-pay';
      } else if (level2 === PayModeLevel2.GooglePay) {
        paymentData.img = '/assets/img/google-pay-logo.svg';
        paymentData.icon = 'fab fa-google-pay';
      }
      else if (level2 === PayModeLevel2.GCash)
      {
        paymentData.img = '/assets/img/gcash.png';
        paymentData.icon = 'fab fa-gcash';
      } 
      else {
        paymentData.img = '/assets/img/icons/icon-wallet.svg';
        paymentData.icon = 'fab fa-cc-visa';
      }
      translate
        .get('SUBSCRIPTIONS.CARD-ENDING-WITH', {
          number: paymentMethod.masked_card_number ? paymentMethod.masked_card_number : paymentMethod.masked_account_number ? paymentMethod.masked_account_number : '****'
        })
        .subscribe((translations: any) => {
          paymentData.number = translations;
        });
      break;

    case PayModeLevel1.Other:
      paymentData.img = '/assets/img/icons/icon-others-method.svg';
      paymentData.icon = 'fab fa-cc-visa';
      paymentData.level1Text = 'Other';
      translate
        .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: '****' })
        .subscribe((translations: any) => {
          paymentData.number = translations;
        });
      break;

    case PayModeLevel1.PayTo:
      paymentData.img = '/assets/img/icons/icon-payto.svg';
      paymentData.icon = 'fab fa-cc-visa';
      paymentData.level1Text = 'PayTo';
      paymentData.number = paymentMethod.pay_to_details;
      break;

    default:
      paymentData.img = '/assets/img/icons/icon-others-method.svg';
      paymentData.icon = 'fab fa-cc-visa';
      paymentData.level1Text = 'Other';
      translate
        .get('SUBSCRIPTIONS.CARD-ENDING-WITH', { number: '****' })
        .subscribe((translations: any) => {
          paymentData.number = translations;
        });
      break;
  }

  return paymentData;
}

/**
 * Get payment level1 text (Card, UPI, NetBanking, etc.)
 * @param paymentMethod - The payment method object
 * @returns String representing the payment level1 type
 */
export function getPaymentLevel1Text(paymentMethod: PaymentMethod): string {
  const level1Str = String(paymentMethod.level1).toLowerCase();
  const level1Num = Number(paymentMethod.level1);

  // Map level1 to display text
  if (level1Num === 1 || level1Str === 'cash') {
    return 'Cash > ';
  }
  if (level1Num === 2 || level1Str === 'card') {
    return 'Card > ';
  }
  if (level1Num === 3 || level1Str === 'netbanking') {
    return 'NetBanking > ';
  }
  if (level1Num === 4 || level1Str === 'upi') {
    return 'UPI > ';
  }
  if (level1Num === 5 || level1Str === 'wallet') {
    return 'Wallet > ';
  }
  if (level1Num === 6 || level1Str === 'ach') {
    return 'ACH > ';
  }
  if (level1Num === 7 || level1Str === 'cheque') {
    return 'Cheque > ';
  }
  if (level1Num === 9 || level1Str === 'emandate') {
    return 'E-Mandate > ';
  }
  if (level1Num === 10 || level1Str === 'adjustment') {
    return 'Adjustment';
  }
  if (level1Num === 11 || level1Str === 'payto') {
    return 'PayTo';
  }

  return 'Other';
}

/**
 * Get formatted display text for payment method (Level1 + Network Type + Masked Number)
 * @param paymentMethod - The payment method object
 * @returns Formatted string for display
 */
export function getPaymentMethodDisplayText(paymentMethod: PaymentMethod): string {
  const parts: string[] = [];

  // Add payment level1 text (Card, UPI, NetBanking, etc.)
  const level1Text = getPaymentLevel1Text(paymentMethod);
  if (level1Text) {
    parts.push(level1Text);
  }

  // Add network_type if available (for cards like Visa, Mastercard)
  if (paymentMethod.network_type) {
    parts.push(paymentMethod.network_type);
  }

  // Add masked number
  if (paymentMethod.masked_card_number) {
    parts.push(`•••• ${paymentMethod.masked_card_number}`);
  } else if (paymentMethod.masked_account_number) {
    parts.push(`•••• ${paymentMethod.masked_account_number}`);
  } else if (paymentMethod.vpa_id) {
    parts.push(paymentMethod.vpa_id);
  }

  return parts.join(' ');
}

